import {
  SecondaryButton,
  IconContentCopy,
  BUTTON_VARIANTS,
  DialogOverlay,
  IndeterminateProgressBar,
} from "@cdkglobal/radial";
import { useUserContext } from "../../../../../contexts/UserContext";
import { DropdownWithTooltip } from "../../../../common/Tooltip";
import {
  getRecordsBySearchFilter,
  getResolvedDynamicRowDataValue,
  getSelectedOption,
  getTranslatedLabel,
  getTypeAheadRequest,
  getStoreOptionByGroupId,
  dispatchUpdateField,
  getAccountingGroupIdOptions,
  getSelectedGroupIdOption,
} from "../../../helpers";
import { useEffect, useState } from "react";
import { useUIStateContext } from "../../../../../contexts/UIStateContext";
import {
  RecordsSearchFilterResponse,
  GenericSearchResponseObject,
  SaveRecordRequest,
  ToastInfo,
  RenderCopyDialogProps,
  PrimitiveValueType,
} from "../../../../../types";
import {
  DEFAULT_TOAST,
  NO_RESULTS_DEFAULT_IOPTION,
  TOAST_MESSAGES,
} from "../../../constants";
import { extractErrorAndMessage } from "../../../validations";
import {
  PO_UOM_ACTIVE_STATUS_FIELD,
  PO_UNITS_OF_MEASURE_DIALOG_FIELDS_SCHEMA,
  PO_UNITS_OF_MEASURE_TYPEAHEAD_CONFIG,
} from "../constants";
import {
  StyledGroupCodesDialog,
  StyledGroupCodesFieldsRow,
  StyledGroupCodesTable,
  StyledHeaderSubTitle,
  StyledFieldWrapper,
} from "../../../../../utils/styled";
import { useRefetchRowDataList, useSaveRecord } from "../../../../../hooks";
import CopyPOUnitsOfMeasureTable from "./CopyPOUnitsOfMeasureTable";
import CopyPOUnitsOfMeasureConfirmationDialog from "./CopyPOUnitsOfMeasureConfirmationDialog";
import { getIntl } from "../../../../../translate/i18n";
import { ampli } from "../../../../../ampli";
import { useTranslation } from "react-i18next";

export default function CopyPOUnitsOfMeasureDialog({
  rowDataReducerState,
  dispatch,
}: Readonly<RenderCopyDialogProps>): JSX.Element {
  const { t, i18n } = useTranslation();
  const { setLandingPageToast } = useUIStateContext();
  const {
    iamUserState,
    setupBaseRequestInfo,
    setupListNavInfoByDepartment,
    selected,
  } = useUserContext();
  const intl = getIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [searchResponse, setSearchResponse] =
    useState<RecordsSearchFilterResponse>({
      message: "",
      data: [],
    });
  const [selectedRows, setSelectedRows] = useState<
    GenericSearchResponseObject[]
  >([]);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);

  const { dialogRowData = {} } = rowDataReducerState;
  const POUnitsOfMeasureDialogPropertyKeys = Object.keys(
    PO_UNITS_OF_MEASURE_DIALOG_FIELDS_SCHEMA,
  );
  const sourceStoreKey = POUnitsOfMeasureDialogPropertyKeys[0];
  const sourceGroupKey = POUnitsOfMeasureDialogPropertyKeys[1];
  const destinationStoreKey = POUnitsOfMeasureDialogPropertyKeys[2];
  const destinationGroupKey = POUnitsOfMeasureDialogPropertyKeys[3];

  const CUSTOM_PO_UOM_KEYS_TO_ADD = [
    sourceStoreKey,
    sourceGroupKey,
    destinationGroupKey,
  ];

  const sourceStoreValue = dialogRowData[sourceStoreKey];
  const destinationStoreValue = dialogRowData[destinationStoreKey];
  const hasSourceStoreProperty = Boolean(
    rowDataReducerState.schema.properties[sourceStoreKey],
  );

  const sourceStore: string | undefined =
    typeof sourceStoreValue === "string" ? sourceStoreValue : undefined;

  const destinationStore: string | undefined =
    typeof destinationStoreValue === "string"
      ? destinationStoreValue
      : undefined;

  const selectedUOM = selectedRows?.[0];

  const formattedSelectedUOMs =
    selectedUOM && typeof selectedUOM.uom_cd === "string"
      ? `Unit of Measure Code: ${
          selectedUOM.uom_cd
        }\nUnit of Measure Description: ${selectedUOM.uom_desc || "N/A"}`
      : "None";

  const getTableHeaderTitle = () => {
    const selectedGroupIdOption = getSelectedGroupIdOption(
      dialogRowData[sourceGroupKey] as string,
      iamUserState.eisGroups.groups,
    );
    return `${intl.formatMessage({
      id: "title.poUnitsOfMeasure",
      defaultMessage: "PO Units of Measure",
    })} ${
      selectedGroupIdOption.label === ""
        ? ""
        : `| ${selectedGroupIdOption.label} | ${intl.formatMessage({
            id: "title.selectedRecords",
            defaultMessage: "Selected Records",
          })} - ${selectedRows.length}`
    }`;
  };

  const handleDialogOpen = () => {
    setIsOpen(true);
    setLandingPageToast(DEFAULT_TOAST);
    ampli.copyPOUnitsOfMeasureDialogPopup({
      "Open Copy PO Units of Measure Dialog Popup": true,
    });
    dispatchUpdateField({
      propertyKey: destinationGroupKey,
      propertySubKey: undefined,
      rowIndex: undefined,
      newFieldValue: selected.groupId.value,
      isDialogField: true,
      selectedTabSectionKey: undefined,
      targetFieldKey: destinationGroupKey,
      dispatch,
    });
  };

  const handleDialogClose = () => {
    setIsConfirmationDialogOpen(false);
    setIsOpen(false);
    setSearchResponse({ message: "", data: [] });
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        dialogRowData: {},
        dialogRowDataErrors: {},
      },
    });
  };

  const getFilteredGroupOptions = (
    propertyKey: string,
    enumValues: PrimitiveValueType[] | undefined,
  ) => {
    const selectedEnumOrStoreOptions =
      enumValues && getAccountingGroupIdOptions(iamUserState.eisGroups.groups);
    if (!selectedEnumOrStoreOptions) return [NO_RESULTS_DEFAULT_IOPTION()];
    if (propertyKey === sourceGroupKey) {
      const filteredOptions = selectedEnumOrStoreOptions.filter(
        (option) => option.value !== selected.groupId.value,
      );
      return filteredOptions.length > 0
        ? filteredOptions
        : [NO_RESULTS_DEFAULT_IOPTION()];
    }
    return selectedEnumOrStoreOptions;
  };

  const onSuccessCallback = (response: ToastInfo): void => {
    setLandingPageToast(response);
    refetchRowDataList();
  };

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel: undefined,
    dispatch,
    setTableTitleInfo: undefined,
  });

  const { mutate: saveRecords } = useSaveRecord({
    onSuccessCallback: onSuccessCallback,
  });

  const copyRecords = () => {
    ampli.copyPOUnitsOfMeasureButtonClick({
      "Copy PO Units of Measure": true,
      "Source Store": sourceStore,
      "Destination Store": destinationStore,
      "Selected PO Units of Measure": formattedSelectedUOMs,
    });
    handleShowConfirmDialog();
    handleDialogClose();
    setLandingPageToast(TOAST_MESSAGES.COPYING);
    const updatedRowData = {
      copy_po_units_of_measure: selectedRows.map((record) => ({
        ...record,
        store_id: selected.store.value,
      })),
    };
    const request: SaveRecordRequest = {
      ...setupBaseRequestInfo,
      record: updatedRowData,
    };
    saveRecords(request);
  };

  const handleShowConfirmDialog = () => {
    setIsConfirmationDialogOpen((prev) => !prev);
  };

  useEffect(() => {
    const fetchResponse = async () => {
      const sourceStore = dialogRowData[sourceStoreKey];
      if (sourceStore) {
        const existingStoreIdKey = PO_UNITS_OF_MEASURE_TYPEAHEAD_CONFIG.storeId;
        const existingStoreIdValue = existingStoreIdKey
          ? (getResolvedDynamicRowDataValue({
              propertyKey: sourceStoreKey,
              dynamicField: existingStoreIdKey,
              rowData: dialogRowData,
            }) as string)
          : undefined;
        const request = getTypeAheadRequest({
          propertyKey: sourceStoreKey,
          typeaheadConfig: PO_UNITS_OF_MEASURE_TYPEAHEAD_CONFIG,
          rowIndex: undefined,
          rowDataReducerState,
          setupBaseRequestInfo,
          existingStoreIdValue,
          iamUserState,
          setupListNavInfoByDepartment: setupListNavInfoByDepartment,
        });
        if (request) {
          setIsLoadingTableData(true);
          const { response } = await getRecordsBySearchFilter(request);
          setSearchResponse(response);
          const defaultSelectedRows =
            response.data.filter(
              (row) => row[PO_UOM_ACTIVE_STATUS_FIELD] === true,
            ) || [];
          setSelectedRows(defaultSelectedRows);
        }
        setIsLoadingTableData(false);
      }
    };
    fetchResponse();
  }, [dialogRowData[sourceStoreKey]]);

  useEffect(() => {
    if (dialogRowData[sourceGroupKey] && hasSourceStoreProperty) {
      const storeValue = getStoreOptionByGroupId({
        selectedGroupId: dialogRowData[sourceGroupKey] as string,
        iamUserState,
      }).value;
      dispatchUpdateField({
        propertyKey: sourceStoreKey,
        propertySubKey: undefined,
        rowIndex: undefined,
        newFieldValue: storeValue,
        isDialogField: true,
        selectedTabSectionKey: undefined,
        targetFieldKey: sourceStoreKey,
        dispatch,
      });
    }
  }, [dialogRowData[sourceGroupKey], hasSourceStoreProperty]);

  useEffect(() => {
    CUSTOM_PO_UOM_KEYS_TO_ADD.forEach((key) => {
      dispatch({
        type: "ADD_SCHEMA_PROPERTY",
        payload: {
          newPropertyKey: key,
          newProperty: PO_UNITS_OF_MEASURE_DIALOG_FIELDS_SCHEMA[key],
        },
      });
    });
  }, []);

  return (
    <>
      <SecondaryButton
        icon={<IconContentCopy />}
        dataTestId="copy-po-units-of-measure-button"
        iconSize={24}
        onClick={handleDialogOpen}
        size="large"
        text={intl.formatMessage({
          id: "label.copy_po_units_of_measure",
          defaultMessage: "Copy PO Units of Measure",
        })}
        variant="secondary"
      />
      {isOpen && (
        <StyledGroupCodesDialog
          id="copy-po-units-of-measure"
          dataTestId="copy-po-units-of-measure"
          buttonsProps={[
            {
              id: "copy-units-of-measure-cancel",
              onClick: handleDialogClose,
              text: intl.formatMessage({
                id: "button.cancel",
                defaultMessage: "Cancel",
              }),
              variant: BUTTON_VARIANTS.TEXT,
            },
            {
              id: "copy-po-units-of-measure-import",
              onClick: handleShowConfirmDialog,
              text: intl.formatMessage({
                id: "button.copy",
                defaultMessage: "Copy",
              }),
              variant: BUTTON_VARIANTS.PRIMARY,
              isDisabled: selectedRows.length === 0,
            },
          ]}
          isOpen={isOpen}
          onClose={handleDialogClose}
          title={intl.formatMessage({
            id: "label.copy_po_units_of_measure",
            defaultMessage: "Copy PO Units of Measure",
          })}
        >
          {isConfirmationDialogOpen && (
            <DialogOverlay>
              <CopyPOUnitsOfMeasureConfirmationDialog
                handleConfirm={copyRecords}
                isConfirmationDialogOpen={isConfirmationDialogOpen}
                handleCancel={handleShowConfirmDialog}
              />
            </DialogOverlay>
          )}
          <StyledGroupCodesFieldsRow>
            {Object.entries(PO_UNITS_OF_MEASURE_DIALOG_FIELDS_SCHEMA)
              .filter(([, { isHidden }]) => !isHidden)
              .map(
                ([
                  propertyKey,
                  {
                    title,
                    description,
                    enum: enumValues,
                    dependentFields,
                    disabled,
                  },
                ]) => {
                  const translatedTitle = getTranslatedLabel(
                    propertyKey,
                    "title",
                    title,
                    t,
                    i18n.language,
                  );

                  const translatedDescription = getTranslatedLabel(
                    propertyKey,
                    "description",
                    description ?? "",
                    t,
                    i18n.language,
                  );

                  const inputValue = dialogRowData[propertyKey];

                  const filteredOptionList = getFilteredGroupOptions(
                    propertyKey,
                    enumValues,
                  );
                  const selectedOption = getSelectedOption({
                    inputValue: inputValue as string,
                    optionsList: filteredOptionList,
                  });
                  const { error, message } = extractErrorAndMessage(
                    propertyKey,
                    undefined,
                    undefined,
                    rowDataReducerState,
                    true,
                  );

                  return (
                    <StyledFieldWrapper key={propertyKey}>
                      <StyledHeaderSubTitle>
                        {translatedDescription}
                      </StyledHeaderSubTitle>
                      <DropdownWithTooltip
                        title={translatedTitle}
                        description={description ?? ""}
                        tooltip={translatedDescription}
                        propertyKey={propertyKey}
                        rowIndex={undefined}
                        propertySubKey={undefined}
                        selectedOption={selectedOption}
                        optionsList={filteredOptionList}
                        companyKey={undefined}
                        storeKey={undefined}
                        dependentFields={dependentFields}
                        evalMethod={undefined}
                        isRequired={true}
                        isDisabled={disabled || false}
                        error={error}
                        message={message}
                        dependencyRules={undefined}
                        triggerDependencyRule={undefined}
                        isDialogField={true}
                        selectedTabSectionKey={undefined}
                        rowDataReducerState={rowDataReducerState}
                        dispatch={dispatch}
                      />
                    </StyledFieldWrapper>
                  );
                },
              )}
          </StyledGroupCodesFieldsRow>
          <StyledGroupCodesTable>
            {isLoadingTableData ? (
              <IndeterminateProgressBar
                dataTestId="indeterminate-progress-bar"
                label={intl.formatMessage({
                  id: "label.fetching",
                  defaultMessage: "Fetching...",
                })}
                loaderSize="large"
                trackWidth="500px"
                style={{ marginTop: "1rem", height: "50vh" }}
              />
            ) : (
              <CopyPOUnitsOfMeasureTable
                headerTitle={getTableHeaderTitle()}
                responseRowData={searchResponse.data}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
              />
            )}
          </StyledGroupCodesTable>
        </StyledGroupCodesDialog>
      )}
    </>
  );
}
